import React, { useState, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const BASEURL = process.env.REACT_APP_BASE_URL;

interface WasteDate {
  date: string;
  quantity: number;
  amount: number;
}

interface WasteType {
  wasteType: string;
  totalQuantity: number;
  totalAmount: number;
  dates: WasteDate[];
}

interface Collector {
  collector: string;
  totalQuantity: number;
  totalAmount: number;
}

interface ApiResponse {
  success: boolean;
  code: number;
  data: {
    totalWasteCollected: number;
    totalAmountCollected: number;
    wasteTypes: WasteType[];
    collectors: Collector[];
  };
  message: string;
  action: string;
}

interface ChartData {
  name: string;
  quantity: number;
  amount: number;
}

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

const Dashboard: React.FC = () => {
  const [data, setData] = useState<ApiResponse["data"] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const fetchData = async (start: Date, end: Date) => {
    try {
      setLoading(true);
      const userToken = localStorage.getItem("auth-token");
      if (!userToken) throw new Error("No authentication token found");

      const formattedStart = encodeURIComponent(
        start.toISOString().split("T")[0]
      );
      const formattedEnd = encodeURIComponent(end.toISOString().split("T")[0]);

      const response = await axios.get(
        `${BASEURL}/v1/fisherman/metrics?startDate=${formattedStart}&endDate=${formattedEnd}`,
        {
          headers: {
            "x-user-token": userToken,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      setData(response.data.data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        window.location.href = "/login";
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchData(startDate, endDate);
    }
  }, [startDate, endDate]);

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (error)
    return <div className="text-center py-8 text-red-500">Error: {error}</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  const wasteTypeData: ChartData[] = data.wasteTypes.map((item) => ({
    name: item.wasteType.toUpperCase(),
    quantity: item.totalQuantity,
    amount: item.totalAmount,
  }));

  return (
    <div
      className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen rounded-lg shadow"
      style={{ marginTop: "44px", borderRadius: "70px" }}
    >
      {/* Header & Date Picker */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
        <h1 className="text-3xl font-extrabold text-gray-900">
          Waste Collection Dashboard
        </h1>
        <div className="flex gap-3 items-center">
          <label className="text-gray-700 font-semibold text-sm">From:</label>
          <DatePicker
            selected={startDate}
            onChange={(date: Date | null) => setStartDate(date)}
            maxDate={new Date()}
            dateFormat="yyyy-MM-dd"
            className="border border-gray-300 px-3 py-1 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholderText="Start Date"
          />
          <label className="text-gray-700 font-semibold text-sm ml-2">
            To:
          </label>
          <DatePicker
            selected={endDate}
            onChange={(date: Date | null) => setEndDate(date)}
            minDate={startDate ?? undefined}
            maxDate={new Date()}
            dateFormat="yyyy-MM-dd"
            className="border border-gray-300 px-3 py-1 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholderText="End Date"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
          <h3 className="text-gray-500 text-sm uppercase tracking-wide mb-1">
            Total Waste Collected
          </h3>
          <p className="text-3xl font-extrabold text-blue-600">
            {data.totalWasteCollected} kg
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
          <h3 className="text-gray-500 text-sm uppercase tracking-wide mb-1">
            Total Amount Paid{" "}
          </h3>
          <p className="text-3xl font-extrabold text-green-600">
            ₹{data.totalAmountCollected}
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
          <h3 className="text-gray-500 text-sm uppercase tracking-wide mb-1">
            Active Collectors
          </h3>
          <p className="text-3xl font-extrabold text-purple-600">
            {data.collectors.length}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
        <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Waste Type Distribution
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={wasteTypeData}
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  dataKey="quantity"
                  nameKey="name"
                  label={({
                    name,
                    percent,
                  }: {
                    name: string;
                    percent?: number;
                  }) =>
                    `${name} ${percent ? (percent * 100).toFixed(0) : "0"}%`
                  }
                >
                  {wasteTypeData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} kg`, "Quantity"]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Purchase / Incentive Value
          </h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={wasteTypeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => [`₹${value}`, "Amount"]} />
                <Bar dataKey="amount" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="overflow-x-auto bg-white p-6 rounded-xl shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-5">
            Waste Details
          </h3>
          <table className="min-w-full border-collapse border border-gray-200 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-left text-gray-600 uppercase">
                  Waste Type
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left text-gray-600 uppercase">
                  Quantity
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left text-gray-600 uppercase">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {data.wasteTypes.map((waste, idx) => (
                <tr
                  key={idx}
                  className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}
                >
                  <td className="border border-gray-300 px-4 py-3">
                    {waste.wasteType.toUpperCase()}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    {waste.totalQuantity}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    ₹{waste.totalAmount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="overflow-x-auto bg-white p-6 rounded-xl shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-5">
            Collectors
          </h3>
          <table className="min-w-full border-collapse border border-gray-200 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-left text-gray-600 uppercase">
                  Collector ID
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left text-gray-600 uppercase">
                  Quantity
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left text-gray-600 uppercase">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {data.collectors.map((collector, idx) => (
                <tr
                  key={idx}
                  className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}
                >
                  <td className="border border-gray-300 px-4 py-3">
                    {collector.collector}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    {collector.totalQuantity}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    ₹{collector.totalAmount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
